import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { useTheme } from './theme-provider';

// Floating particles component
function FloatingParticles({ count = 100 }) {
  const mesh = useRef<THREE.Points>(null);
  const { theme } = useTheme();
  const positions = new Float32Array(count * 3);

  // Generate random positions
  for (let i = 0; i < count * 3; i += 3) {
    positions[i] = (Math.random() - 0.5) * 20;
    positions[i + 1] = (Math.random() - 0.5) * 20;
    positions[i + 2] = (Math.random() - 0.5) * 20;
  }

  useFrame((state) => {
    if (mesh.current) {
      mesh.current.rotation.y += 0.001;
      mesh.current.rotation.x += 0.0005;
    }
  });

  return (
    <points ref={mesh}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.05}
        color={theme === 'dark' ? '#6366f1' : '#3b82f6'}
        transparent
        opacity={0.6}
        sizeAttenuation
      />
    </points>
  );
}

// Animated geometric shapes
function AnimatedShapes() {
  const groupRef = useRef<THREE.Group>(null);
  const { theme } = useTheme();

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += 0.005;
      groupRef.current.rotation.x += 0.002;
    }
  });

  const colors = theme === 'dark' 
    ? ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981'] 
    : ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981'];

  return (
    <group ref={groupRef}>
      {/* Floating cube */}
      <mesh position={[3, 2, -5]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial
          color={colors[0]}
          transparent
          opacity={0.3}
          wireframe
        />
      </mesh>

      {/* Floating sphere */}
      <mesh position={[-3, -2, -3]}>
        <sphereGeometry args={[0.8, 16, 16]} />
        <meshStandardMaterial
          color={colors[1]}
          transparent
          opacity={0.4}
          wireframe
        />
      </mesh>

      {/* Floating torus */}
      <mesh position={[0, 3, -7]}>
        <torusGeometry args={[1, 0.3, 8, 16]} />
        <meshStandardMaterial
          color={colors[2]}
          transparent
          opacity={0.3}
          wireframe
        />
      </mesh>

      {/* Floating cylinder */}
      <mesh position={[4, -3, -4]}>
        <cylinderGeometry args={[0.5, 0.5, 2, 8]} />
        <meshStandardMaterial
          color={colors[3]}
          transparent
          opacity={0.4}
          wireframe
        />
      </mesh>
    </group>
  );
}

// Pulsing welcome text
function WelcomeText() {
  const textRef = useRef<THREE.Mesh>(null);
  const { theme } = useTheme();

  useFrame((state) => {
    if (textRef.current) {
      textRef.current.scale.setScalar(1 + Math.sin(state.clock.elapsedTime * 2) * 0.05);
    }
  });

  return (
    <mesh ref={textRef} position={[0, 0, 2]}>
      <sphereGeometry args={[0.3, 16, 16]} />
      <meshStandardMaterial
        color={theme === 'dark' ? '#6366f1' : '#3b82f6'}
        transparent
        opacity={0.6}
        emissive={theme === 'dark' ? '#6366f1' : '#3b82f6'}
        emissiveIntensity={0.2}
      />
    </mesh>
  );
}

// Main scene component
function LoginScene() {
  const { theme } = useTheme();

  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={theme === 'dark' ? 0.4 : 0.6} />
      <pointLight 
        position={[10, 10, 10]} 
        intensity={0.8} 
        color={theme === 'dark' ? '#6366f1' : '#3b82f6'} 
      />
      <pointLight 
        position={[-10, -10, -10]} 
        intensity={0.6} 
        color={theme === 'dark' ? '#8b5cf6' : '#8b5cf6'} 
      />
      
      {/* Subtle gradient background effect */}
      <mesh position={[0, 0, -10]}>
        <planeGeometry args={[20, 20]} />
        <meshBasicMaterial 
          color={theme === 'dark' ? '#1e1b4b' : '#f8fafc'} 
          transparent 
          opacity={0.1}
        />
      </mesh>
      
      {/* Animated components */}
      <FloatingParticles count={150} />
      <AnimatedShapes />
      <WelcomeText />
      
      {/* Subtle camera controls */}
      <OrbitControls
        enableZoom={false}
        enablePan={false}
        enableRotate={true}
        autoRotate
        autoRotateSpeed={0.5}
        maxPolarAngle={Math.PI / 2}
        minPolarAngle={Math.PI / 2}
      />
    </>
  );
}

// Main export component
export function LoginBackground3D() {
  const { theme } = useTheme();
  
  return (
    <div className="fixed inset-0 -z-10">
      <Canvas
        camera={{ position: [0, 0, 8], fov: 75 }}
        style={{ 
          background: theme === 'dark' 
            ? 'radial-gradient(ellipse at center, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.05) 50%, transparent 100%)'
            : 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 50%, transparent 100%)'
        }}
      >
        <LoginScene />
      </Canvas>
    </div>
  );
}
